import { useState, useCallback, useMemo, useTransition } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useUser } from '@/constants/UserContext';
import { usePublicEvents } from '@/hooks/usePublicEvents';
import { useAcceptedMembershipOrgIds } from '@/hooks/useAcceptedMembershipOrgIds';
import { useUserEvents } from '@/hooks/useUserEvents';
import { useCurrentActivities } from '@/hooks/useCurrentActivities';
import type { Organization } from '@/types/events';
import type { Location } from './components/FilterModal';

const URL_KEYS = {
  search: 'q',
  status: 'status',
  startDate: 'start',
  endDate: 'end',
} as const;

function parseDate(s: string | null): Date | null {
  if (!s) return null;
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

function filterEventsByStatus(
  events: Organization[],
  status: 'Scheduled' | 'Active'
): Organization[] {
  if (status === 'Scheduled') {
    return events.filter((e) => e.start_date && e.end_date);
  }
  return events.filter(
    (e) => !e.start_date || !e.end_date || e.status === 'Active'
  );
}

export function useOrganizationsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isPending, startTransition] = useTransition();

  const { user: authUser } = useAuth();
  const { user } = useUser();
  const userId = authUser?.id ?? null;
  const userRole = user?.role ?? 'volunteer';
  const isOrganization = userRole === 'organization';
  const subscriptionActive = user?.subscription !== false;

  // URL-synced filters (read from URL on mount, write on change)
  const searchQuery = searchParams.get(URL_KEYS.search) ?? '';
  const eventStatusFilter = (searchParams.get(URL_KEYS.status) as 'Scheduled' | 'Active') ?? 'Scheduled';
  const startDate = useMemo(
    () => parseDate(searchParams.get(URL_KEYS.startDate)),
    [searchParams]
  );
  const endDate = useMemo(
    () => parseDate(searchParams.get(URL_KEYS.endDate)),
    [searchParams]
  );

  const setSearchQuery = useCallback(
    (value: string | ((prev: string) => string)) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          const v = typeof value === 'function' ? value(prev.get(URL_KEYS.search) ?? '') : value;
          if (v) next.set(URL_KEYS.search, v);
          else next.delete(URL_KEYS.search);
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const setEventStatusFilter = useCallback(
    (value: 'Scheduled' | 'Active' | ((prev: 'Scheduled' | 'Active') => 'Scheduled' | 'Active')) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          const v = typeof value === 'function' ? value((prev.get(URL_KEYS.status) as 'Scheduled' | 'Active') ?? 'Scheduled') : value;
          next.set(URL_KEYS.status, v);
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  // Modal state
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showEventPreview, setShowEventPreview] = useState(false);
  const [editingEvent, setEditingEvent] = useState<Organization | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<Organization | null>(null);

  // Location filter state (not in URL - complex structure)
  const [selectedLocations, setSelectedLocations] = useState<Location[]>([]);
  const [tempSelectedLocations, setTempSelectedLocations] = useState<Location[]>([]);
  const [isLocationPickerLoading, setIsLocationPickerLoading] = useState(false);

  const {
    events: publicEvents,
    loading: publicLoading,
    hasMore,
    loadingMore,
    loadMore,
    refetch: refetchPublicEvents,
  } = usePublicEvents(userId);
  const { events: userEvents, loading: userEventsLoading, refetch: refetchUserEvents } = useUserEvents(
    isOrganization ? userId : null
  );
  const { activities, refetch: refetchActivities } = useCurrentActivities(isOrganization ? null : userId);
  const { orgIds: membershipOrgIds, refetch: refetchMembershipOrgIds } = useAcceptedMembershipOrgIds(
    isOrganization ? null : userId
  );

  // Where the preview opens: the top, or scrolled to the event's shifts (the card's Shifts
  // button).
  const [previewSection, setPreviewSection] = useState<'shifts' | null>(null);

  const handleShiftsPress = useCallback((event: Organization) => {
    setPreviewSection('shifts');
    setSelectedEvent(event);
    setShowEventPreview(true);
  }, []);

  const handleEventPress = useCallback((event: Organization) => {
    setPreviewSection(null);
    setSelectedEvent(event);
    setShowEventPreview(true);
  }, []);

  const handleEditEvent = useCallback((event: Organization) => {
    setEditingEvent(event);
    setShowEditModal(true);
  }, []);

  const handleManageEvent = useCallback(
    (event: Organization) => {
      navigate(`/volunteers?eventId=${event.id}`);
    },
    [navigate]
  );

  const handleNavigateToVolunteers = useCallback(
    (eventId: string) => navigate(`/volunteers?eventId=${eventId}`),
    [navigate]
  );

  const handleCreateClick = useCallback(() => setShowCreateModal(true), []);
  const handleCreateSuccess = useCallback(() => {
    refetchUserEvents();
  }, [refetchUserEvents]);
  const handleEditSuccess = useCallback(() => {
    refetchUserEvents();
  }, [refetchUserEvents]);

  const handleFilterClick = useCallback(() => setShowFilterModal(true), []);

  const handleFilterApply = useCallback(
    (filters: { startDate: Date | null; endDate: Date | null; selectedLocations: Location[] }) => {
      startTransition(() => {
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            if (filters.startDate) next.set(URL_KEYS.startDate, filters.startDate.toISOString().split('T')[0]);
            else next.delete(URL_KEYS.startDate);
            if (filters.endDate) next.set(URL_KEYS.endDate, filters.endDate.toISOString().split('T')[0]);
            else next.delete(URL_KEYS.endDate);
            return next;
          },
          { replace: true }
        );
        setSelectedLocations(filters.selectedLocations);
        setTempSelectedLocations(filters.selectedLocations);
      });
    },
    [setSearchParams]
  );

  const handleLocationPickerOpen = useCallback(() => {
    setIsLocationPickerLoading(true);
    setShowFilterModal(false);
    startTransition(() => {
      setShowLocationPicker(true);
      setIsLocationPickerLoading(false);
    });
  }, []);

  const handleLocationSelect = useCallback((locations: Location[]) => {
    setTempSelectedLocations(locations);
    setSelectedLocations(locations);
    setShowLocationPicker(false);
    startTransition(() => setShowFilterModal(true));
  }, []);

  const filteredPublicEvents = useMemo(() => {
    let filtered = [...publicEvents];
    if (startDate && endDate) {
      filtered = filtered.filter((event) => {
        if (!event.start_date || !event.end_date) return false;
        const eventStart = new Date(event.start_date);
        const eventEnd = new Date(event.end_date);
        return eventStart >= startDate && eventEnd <= endDate;
      });
    }
    if (selectedLocations.length > 0) {
      filtered = filtered.filter((event) =>
        selectedLocations.some((loc) =>
          event.location.toLowerCase().includes(loc.name.toLowerCase())
        )
      );
    }
    return filtered;
  }, [publicEvents, startDate, endDate, selectedLocations]);

  const filteredByStatus = useMemo(
    () => filterEventsByStatus(filteredPublicEvents, eventStatusFilter),
    [filteredPublicEvents, eventStatusFilter]
  );

  // For volunteers: events from orgs they're in (activities or accepted membership), excluding current activities
  const newOrgEvents = useMemo(() => {
    if (isOrganization) return [];
    if (activities.length === 0 && membershipOrgIds.length === 0) return [];
    const activeEventIds = new Set(activities.map((a) => a.id));
    const orgIds = new Set([
      ...activities.map((a) => a.organization_id).filter(Boolean),
      ...membershipOrgIds,
    ]);
    if (orgIds.size === 0) return [];
    return publicEvents.filter(
      (e) => e.created_by && orgIds.has(e.created_by) && !activeEventIds.has(e.id)
    );
  }, [isOrganization, activities, membershipOrgIds, publicEvents]);

  const newOrgEventsSectionHeading = useMemo(() => {
    if (newOrgEvents.length === 0) return 'New events from your organizations';
    const orgIdsInSection = new Set(
      newOrgEvents.map((e) => e.created_by).filter((id): id is string => Boolean(id))
    );
    const names: string[] = [];
    const seen = new Set<string>();
    const membershipNameByOrgId = new Map(
      activities
        .filter((a) => a.organization_id && a.organization_name)
        .map((a) => [a.organization_id as string, a.organization_name as string])
    );
    for (const orgId of orgIdsInSection) {
      const n = membershipNameByOrgId.get(orgId);
      if (n && !seen.has(orgId)) {
        seen.add(orgId);
        names.push(n);
      }
    }
    if (names.length === 0) return 'New events from your organizations';
    if (names.length === 1) return `New events from ${names[0]}`;
    if (names.length === 2) return `New events from ${names[0]} and ${names[1]}`;
    return `New events from ${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
  }, [newOrgEvents, activities]);

  return {
    userId,
    userRole,
    isOrganization,
    subscriptionActive,
    searchQuery,
    setSearchQuery,
    eventStatusFilter,
    setEventStatusFilter,
    startDate,
    endDate,
    selectedLocations,
    tempSelectedLocations,
    showFilterModal,
    setShowFilterModal,
    showLocationPicker,
    setShowLocationPicker,
    showCreateModal,
    setShowCreateModal,
    showEditModal,
    setShowEditModal,
    showEventPreview,
    setShowEventPreview,
    editingEvent,
    setEditingEvent,
    selectedEvent,
    setSelectedEvent,
    isLocationPickerLoading,
    publicEvents,
    publicLoading,
    hasMore,
    loadingMore,
    loadMore,
    refetchPublicEvents,
    userEvents,
    userEventsLoading,
    refetchUserEvents,
    activities,
    refetchActivities,
    refetchMembershipOrgIds,
    filteredPublicEvents,
    filteredByStatus,
    newOrgEvents,
    newOrgEventsSectionHeading,
    handleEventPress,
    handleShiftsPress,
    previewSection,
    setPreviewSection,
    handleEditEvent,
    handleManageEvent,
    handleCreateClick,
    handleCreateSuccess,
    handleEditSuccess,
    handleFilterClick,
    handleFilterApply,
    handleLocationPickerOpen,
    handleLocationSelect,
    handleNavigateToVolunteers,
    isPending,
  };
}

export type OrganizationsPageReturn = ReturnType<typeof useOrganizationsPage>;
